import React, { useState, useEffect } from 'react';

interface ConnectionStatusIndicatorProps {
  apiUrl?: string;
  checkInterval?: number;
  className?: string;
}

const ConnectionStatusIndicator: React.FC<ConnectionStatusIndicatorProps> = ({
  apiUrl,
  checkInterval = 30000, // Check every 30 seconds by default
  className = ''
}) => {
  const [isConnected, setIsConnected] = useState<boolean>(false);
  const [isChecking, setIsChecking] = useState<boolean>(true);

  const checkConnection = async (): Promise<boolean> => {
    if (!apiUrl) {
      return false;
    }

    try {
      setIsChecking(true);
      const response = await fetch(`${apiUrl}/health`, {
        method: 'GET',
        timeout: 5000 // 5 second timeout
      } as RequestInit);
      
      return response.ok && response.status === 200;
    } catch (error) {
      console.warn('Connection check failed:', error);
      return false;
    } finally {
      setIsChecking(false);
    }
  };

  useEffect(() => {
    let intervalId: NodeJS.Timeout;

    const performCheck = async () => {
      const connected = await checkConnection();
      setIsConnected(connected);
    };

    // Initial check
    performCheck();

    // Set up periodic checks if checkInterval is provided
    if (checkInterval > 0) {
      intervalId = setInterval(performCheck, checkInterval);
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [apiUrl, checkInterval]);

  const statusClass = isConnected ? 'bg-green-500' : 'bg-yellow-500';
  const statusLabel = isConnected ? 'Connected' : 'Disconnected';

  return (
    <span
      data-testid="connection-status-indicator"
      className={`
        inline-block w-2 h-2 rounded-full ml-2
        ${statusClass}
        ${className}
      `}
      role="status"
      aria-live="polite"
      aria-label={statusLabel}
      tabIndex={0}
      title={statusLabel}
    />
  );
};

export default ConnectionStatusIndicator;